import { defineAsyncComponent } from "vue";
import type { Component } from 'vue';
import FwbSpinner from "@/new_design/components/storybook/components/FwbSpinner/FwbSpinner.vue";

interface CacheOptions {
  maxSize: number;
  timeout: number;
}

class IconCache {
  private cache: Map<string, Component>;
  private accessOrder: string[];
  private readonly maxSize: number;
  private readonly timeout: number;

  constructor(options: CacheOptions = { maxSize: 100, timeout: 3000 }) {
    this.cache = new Map();
    this.accessOrder = [];
    this.maxSize = options.maxSize;
    this.timeout = options.timeout;
  }

  get(key: string): Component | undefined {
    const item = this.cache.get(key);
    if (item) {
      this.updateAccessOrder(key);
    }
    return item;
  }

  set(key: string, value: Component): void {
    if (this.cache.size >= this.maxSize) {
      const lruKey = this.accessOrder[0];
      this.cache.delete(lruKey);
      this.accessOrder.shift();
    }

    this.cache.set(key, value);
    this.updateAccessOrder(key);
  }

  private updateAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
    this.accessOrder.push(key);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
  }

  getSize(): number {
    return this.cache.size;
  }

  getCacheKeys(): string[] {
    return Array.from(this.cache.keys());
  }
}

const iconCache = new IconCache();

const handleIconError = (error: Error, icon: string): Component => {
  console.error(`Error loading icon ${icon}:`, error);
  return FwbSpinner;
};

export const getFBIcon = (icon: string): Component => {
  try {
    if (iconCache.has(icon)) {
      return iconCache.get(icon)!;
    }

    const AsyncComp = defineAsyncComponent({
      loader: async () => {
        try {
          const component = await import(`@/assets/flowbite_icons/${icon}.svg`);
          iconCache.set(icon, component.default);
          return component.default;
        } catch (error) {
          return handleIconError(error as Error, icon);
        }
      },
      loadingComponent: FwbSpinner,
      delay: 0,
      errorComponent: FwbSpinner,
      timeout: 3000,
    });

    iconCache.set(icon, AsyncComp);
    return AsyncComp;
  } catch (error) {
    console.error(`Error in getFBIcon for icon ${icon}:`, error);
    return FwbSpinner;
  }
};

export const debugCache = {
  getSize: () => iconCache.getSize(),
  clearCache: () => iconCache.clear(),
  getCacheContents: () => iconCache.getCacheKeys(),
};

export const getImage = (path: string) =>
  new URL(`/src/assets/icons/${path}`, import.meta.url).href;

export const getAsset = (path: string) =>
  new URL(`/src/assets/${path}`, import.meta.url).href;

export const getIcon = (icon: string) =>
  defineAsyncComponent(() => import(`@/assets/icons/${icon}.svg`));

export const getIconD = (icon: string) =>
  defineAsyncComponent(() => import(`@/assets/icons_new/${icon}.svg`));
